import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Alert,
  ScrollView,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../api/supabase';
import theme from '../../theme';

interface ImportResult {
  type: string;
  total: number;
  successful: number;
  failed: number;
  errors: string[];
  vehicleMatches?: number;
  driverMatches?: number;
}

interface ProgressState {
  isProcessing: boolean;
  currentStep: string;
  progress: number;
  results: ImportResult[];
}

export const DataImportScreen = () => {
  const [progressState, setProgressState] = useState<ProgressState>({
    isProcessing: false,
    currentStep: '',
    progress: 0,
    results: [],
  });

  const user = useAuthStore((state) => state.user);

  // Update progress
  const updateProgress = (step: string, progress: number) => {
    setProgressState(prev => ({
      ...prev,
      currentStep: step,
      progress,
    }));
  };

  // Parse CSV content
  const parseCSVContent = (content: string): any[] => {
    const lines = content.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;

      for (let j = 0; j < lines[i].length; j++) {
        const char = lines[i][j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      if (values.length === headers.length) {
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index]?.replace(/"/g, '') || '';
        });
        data.push(row);
      }
    }

    return data;
  };

  // Process Salik data
  const processSalikData = async (data: any[]): Promise<ImportResult> => {
    const result: ImportResult = {
      type: 'Salik Tolls',
      total: data.length,
      successful: 0,
      failed: 0,
      errors: [],
      vehicleMatches: 0,
      driverMatches: 0,
    };

    try {
      // Get vehicles for matching
      updateProgress('Loading vehicles for matching...', 10);
      const { data: vehicles } = await supabase
        .from('vehicles')
        .select('id, license_plate, assigned_driver_id');

      const vehicleMap = new Map();
      vehicles?.forEach(v => {
        const cleanPlate = v.license_plate.replace(/\s+/g, '').toUpperCase();
        vehicleMap.set(cleanPlate, v);
      });

      updateProgress('Processing Salik transactions...', 30);

      const batchSize = 100;
      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);
        const processedBatch = [];

        for (const record of batch) {
          try {
            // Parse date from "Expense Date" (e.g., "01-APR-2025 19:57:57")
            const expenseDate = record['Expense Date'] || '';
            let transactionDate = null;
            let transactionTime = null;

            if (expenseDate.match(/^\d{2}-[A-Z]{3}-\d{4}\s+\d{2}:\d{2}:\d{2}$/)) {
              const [datePart, timePart] = expenseDate.split(' ');
              transactionDate = datePart;
              transactionTime = timePart;
            }

            const plateNumber = (record['Plate Number'] || '').toString().replace(/\s+/g, '').toUpperCase();
            const amount = parseFloat(record['Amount'] || '0') || 0;
            const tollGate = record['Location'] || '';

            // Match with vehicle
            const vehicle = vehicleMap.get(plateNumber);
            if (vehicle) {
              result.vehicleMatches++;
              if (vehicle.assigned_driver_id) {
                result.driverMatches++;
              }
            }

            processedBatch.push({
              transaction_date: transactionDate,
              transaction_time: transactionTime,
              license_plate: plateNumber,
              toll_gate_name: tollGate,
              toll_gate_location: null,
              amount: amount,
              vehicle_id: vehicle?.id || null,
              driver_id: vehicle?.assigned_driver_id || null,
              assigned_automatically: !!vehicle,
              assignment_verified: false,
              assignment_confidence: vehicle ? 95 : 0,
              payment_status: 'unpaid',
              processed_to_accounting: false,
              source_file: 'mobile_app_import',
              batch_reference: `MOBILE_IMPORT_${Date.now()}`,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });

          } catch (error) {
            result.errors.push(`Row ${i + batch.indexOf(record) + 1}: ${error.message}`);
            result.failed++;
            continue;
          }
        }

        // Insert batch
        if (processedBatch.length > 0) {
          const { error } = await supabase
            .from('salik_tolls')
            .insert(processedBatch);

          if (error) {
            result.errors.push(`Batch ${Math.floor(i / batchSize) + 1}: ${error.message}`);
            result.failed += processedBatch.length;
          } else {
            result.successful += processedBatch.length;
          }
        }

        // Update progress
        const progress = 30 + (60 * (i + batchSize)) / data.length;
        updateProgress(`Processing batch ${Math.floor(i / batchSize) + 1}...`, progress);
      }

    } catch (error) {
      result.errors.push(`Processing error: ${error.message}`);
    }

    return result;
  };

  // Process Traffic Fines data
  const processTrafficFinesData = async (data: any[]): Promise<ImportResult> => {
    const result: ImportResult = {
      type: 'Traffic Fines',
      total: data.length,
      successful: 0,
      failed: 0,
      errors: [],
      vehicleMatches: 0,
      driverMatches: 0,
    };

    try {
      // Get vehicles for matching
      updateProgress('Loading vehicles for fines matching...', 10);
      const { data: vehicles } = await supabase
        .from('vehicles')
        .select('id, license_plate, assigned_driver_id');

      const vehicleMap = new Map();
      vehicles?.forEach(v => {
        const cleanPlate = v.license_plate.replace(/\s+/g, '').toUpperCase();
        vehicleMap.set(cleanPlate, v);
      });

      updateProgress('Processing traffic fines...', 30);

      const batchSize = 100;
      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);
        const processedBatch = [];

        for (const record of batch) {
          try {
            const plateNumber = (record['License Plate'] || record['Plate Number'] || '').toString().replace(/\s+/g, '').toUpperCase();
            const amount = parseFloat(record['Fine Amount'] || record['Amount'] || '0') || 0;
            const violationDate = record['Violation Date'] || record['Date'] || null;
            const location = record['Location'] || record['Violation Location'] || '';
            const violationType = record['Violation Type'] || record['Type'] || '';

            // Match with vehicle
            const vehicle = vehicleMap.get(plateNumber);
            if (vehicle) {
              result.vehicleMatches++;
              if (vehicle.assigned_driver_id) {
                result.driverMatches++;
              }
            }

            processedBatch.push({
              violation_date: violationDate,
              license_plate: plateNumber,
              violation_type: violationType,
              violation_location: location,
              fine_amount: amount,
              vehicle_id: vehicle?.id || null,
              driver_id: vehicle?.assigned_driver_id || null,
              assigned_automatically: !!vehicle,
              assignment_verified: false,
              assignment_confidence: vehicle ? 95 : 0,
              payment_status: 'unpaid',
              processed_to_accounting: false,
              source_file: 'mobile_app_import',
              batch_reference: `MOBILE_IMPORT_${Date.now()}`,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });

          } catch (error) {
            result.errors.push(`Row ${i + batch.indexOf(record) + 1}: ${error.message}`);
            result.failed++;
          }
        }

        // Insert batch
        if (processedBatch.length > 0) {
          const { error } = await supabase
            .from('traffic_fines')
            .insert(processedBatch);

          if (error) {
            result.errors.push(`Batch ${Math.floor(i / batchSize) + 1}: ${error.message}`);
            result.failed += processedBatch.length;
          } else {
            result.successful += processedBatch.length;
          }
        }

        const progress = 30 + (60 * (i + batchSize)) / data.length;
        updateProgress(`Processing fines batch ${Math.floor(i / batchSize) + 1}...`, progress);
      }

    } catch (error) {
      result.errors.push(`Processing error: ${error.message}`);
    }

    return result;
  };

  // Process Uber data
  const processUberData = async (data: any[]): Promise<ImportResult> => {
    const result: ImportResult = {
      type: 'Uber Income',
      total: data.length,
      successful: 0,
      failed: 0,
      errors: [],
      driverMatches: 0,
    };

    try {
      // Get drivers with Uber UUIDs
      updateProgress('Loading drivers for Uber matching...', 10);
      const { data: drivers } = await supabase
        .from('drivers')
        .select('id, uber_uuid, name, phone')
        .not('uber_uuid', 'is', null);

      const uberMap = new Map();
      drivers?.forEach(d => {
        if (d.uber_uuid) {
          uberMap.set(d.uber_uuid, d);
        }
      });

      updateProgress('Processing Uber income data...', 30);

      const batchSize = 100;
      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);
        const processedBatch = [];

        for (const record of batch) {
          try {
            const driverUuid = record['Driver UUID'] || record['driver_uuid'] || '';
            const tripDate = record['Trip Date'] || record['date'] || null;
            const grossAmount = parseFloat(record['Gross Amount'] || record['gross_amount'] || '0') || 0;
            const netAmount = parseFloat(record['Net Amount'] || record['net_amount'] || '0') || 0;
            const commission = parseFloat(record['Commission'] || record['commission'] || '0') || 0;

            // Match with driver
            const driver = uberMap.get(driverUuid);
            if (driver) {
              result.driverMatches++;
            }

            processedBatch.push({
              trip_date: tripDate,
              driver_uuid: driverUuid,
              driver_id: driver?.id || null,
              gross_amount: grossAmount,
              net_amount: netAmount,
              commission: commission,
              assigned_automatically: !!driver,
              assignment_verified: false,
              assignment_confidence: driver ? 95 : 0,
              processed_to_accounting: false,
              source_file: 'mobile_app_import',
              batch_reference: `MOBILE_IMPORT_${Date.now()}`,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });

          } catch (error) {
            result.errors.push(`Row ${i + batch.indexOf(record) + 1}: ${error.message}`);
            result.failed++;
          }
        }

        // Insert batch
        if (processedBatch.length > 0) {
          const { error } = await supabase
            .from('uber_income_raw')
            .insert(processedBatch);

          if (error) {
            result.errors.push(`Batch ${Math.floor(i / batchSize) + 1}: ${error.message}`);
            result.failed += processedBatch.length;
          } else {
            result.successful += processedBatch.length;
          }
        }

        const progress = 30 + (60 * (i + batchSize)) / data.length;
        updateProgress(`Processing Uber batch ${Math.floor(i / batchSize) + 1}...`, progress);
      }

    } catch (error) {
      result.errors.push(`Processing error: ${error.message}`);
    }

    return result;
  };

  // Process Careem data  
  const processCareemData = async (data: any[]): Promise<ImportResult> => {
    const result: ImportResult = {
      type: 'Careem Income',
      total: data.length,
      successful: 0,
      failed: 0,
      errors: [],
      driverMatches: 0,
    };

    try {
      // Get drivers with Careem Captain IDs
      updateProgress('Loading drivers for Careem matching...', 10);
      const { data: drivers } = await supabase
        .from('drivers')
        .select('id, careem_captain_id, name, phone')
        .not('careem_captain_id', 'is', null);

      const careemMap = new Map();
      drivers?.forEach(d => {
        if (d.careem_captain_id) {
          careemMap.set(d.careem_captain_id.toString(), d);
        }
      });

      updateProgress('Processing Careem income data...', 30);

      const batchSize = 100;
      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);
        const processedBatch = [];

        for (const record of batch) {
          try {
            const captainId = (record['Captain ID'] || record['captain_id'] || '').toString();
            const tripDate = record['Trip Date'] || record['date'] || null;
            const grossAmount = parseFloat(record['Gross Amount'] || record['gross_amount'] || '0') || 0;
            const netAmount = parseFloat(record['Net Amount'] || record['net_amount'] || '0') || 0;
            const commission = parseFloat(record['Commission'] || record['commission'] || '0') || 0;

            // Match with driver
            const driver = careemMap.get(captainId);
            if (driver) {
              result.driverMatches++;
            }

            processedBatch.push({
              trip_date: tripDate,
              captain_id: captainId,
              driver_id: driver?.id || null,
              gross_amount: grossAmount,
              net_amount: netAmount,
              commission: commission,
              assigned_automatically: !!driver,
              assignment_verified: false,
              assignment_confidence: driver ? 95 : 0,
              processed_to_accounting: false,
              source_file: 'mobile_app_import',
              batch_reference: `MOBILE_IMPORT_${Date.now()}`,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });

          } catch (error) {
            result.errors.push(`Row ${i + batch.indexOf(record) + 1}: ${error.message}`);
            result.failed++;
          }
        }

        // Insert batch  
        if (processedBatch.length > 0) {
          const { error } = await supabase
            .from('careem_income_raw')
            .insert(processedBatch);

          if (error) {
            result.errors.push(`Batch ${Math.floor(i / batchSize) + 1}: ${error.message}`);
            result.failed += processedBatch.length;
          } else {
            result.successful += processedBatch.length;
          }
        }

        const progress = 30 + (60 * (i + batchSize)) / data.length;
        updateProgress(`Processing Careem batch ${Math.floor(i / batchSize) + 1}...`, progress);
      }

    } catch (error) {
      result.errors.push(`Processing error: ${error.message}`);
    }

    return result;
  };

  // Detect file type and process accordingly
  const processFile = async (content: string, fileName: string) => {
    setProgressState({
      isProcessing: true,
      currentStep: 'Analyzing file...',
      progress: 0,
      results: [],
    });

    try {
      const data = parseCSVContent(content);
      
      if (data.length === 0) {
        Alert.alert('Error', 'No valid data found in the file');
        return;
      }

      // Detect file type based on headers
      const headers = Object.keys(data[0]).map(h => h.toLowerCase());
      let result: ImportResult;

      if (headers.includes('expense date') && headers.includes('plate number') && headers.includes('location')) {
        // Salik file
        updateProgress('Detected Salik file', 5);
        result = await processSalikData(data);
      } else if (headers.includes('driver uuid') || headers.includes('driver_uuid')) {
        // Uber file
        updateProgress('Detected Uber file', 5);
        result = await processUberData(data);
      } else if (headers.includes('captain id') || headers.includes('captain_id')) {
        // Careem file
        updateProgress('Detected Careem file', 5);
        result = await processCareemData(data);
      } else if (headers.includes('fine amount') || headers.includes('violation')) {
        // Traffic fines file
        updateProgress('Detected Traffic Fines file', 5);
        result = await processTrafficFinesData(data);
      } else {
        Alert.alert('Error', 'Unable to detect file type. Please ensure your CSV has the correct headers.');
        return;
      }

      updateProgress('Import completed!', 100);
      setProgressState(prev => ({
        ...prev,
        isProcessing: false,
        results: [result],
      }));

      // Show completion alert
      Alert.alert(
        'Import Complete',
        `${result.type}: ${result.successful}/${result.total} records imported successfully`
      );

    } catch (error) {
      Alert.alert('Error', `Failed to process file: ${error.message}`);
      setProgressState(prev => ({ ...prev, isProcessing: false }));
    }
  };

  // Handle file selection
  const handleFileUpload = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['text/csv', 'application/vnd.ms-excel'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        
        // Read file content
        const response = await fetch(file.uri);
        const content = await response.text();
        
        // Process the file
        await processFile(content, file.name);
      }
    } catch (error) {
      Alert.alert('Error', `Failed to upload file: ${error.message}`);
    }
  }, []);

  // Render results
  const renderResults = () => {
    if (progressState.results.length === 0) return null;

    return (
      <View style={{ marginTop: 24 }}>
        <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 16, color: theme.colors.textPrimary }}>
          Import Results
        </Text>
        
        {progressState.results.map((result, index) => (
          <View key={index} style={{
            backgroundColor: theme.colors.white,
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            borderWidth: 1,
            borderColor: theme.colors.gray200,
          }}>
            <Text style={{ fontSize: 16, fontWeight: '600', marginBottom: 12, color: theme.colors.textPrimary }}>
              {result.type}
            </Text>
            
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
              <View style={{ alignItems: 'center' }}>
                <Text style={{ fontSize: 24, fontWeight: 'bold', color: theme.colors.success }}>
                  {result.successful}
                </Text>
                <Text style={{ fontSize: 12, color: theme.colors.gray600 }}>Success</Text>
              </View>
              
              <View style={{ alignItems: 'center' }}>
                <Text style={{ fontSize: 24, fontWeight: 'bold', color: theme.colors.error }}>
                  {result.failed}
                </Text>
                <Text style={{ fontSize: 12, color: theme.colors.gray600 }}>Failed</Text>
              </View>
              
              <View style={{ alignItems: 'center' }}>
                <Text style={{ fontSize: 24, fontWeight: 'bold', color: theme.colors.textPrimary }}>
                  {result.total}
                </Text>
                <Text style={{ fontSize: 12, color: theme.colors.gray600 }}>Total</Text>
              </View>
              
              {result.vehicleMatches !== undefined && (
                <View style={{ alignItems: 'center' }}>
                  <Text style={{ fontSize: 24, fontWeight: 'bold', color: theme.colors.primary }}>
                    {result.vehicleMatches}
                  </Text>
                  <Text style={{ fontSize: 12, color: theme.colors.gray600 }}>Vehicle Matches</Text>
                </View>
              )}
              
              {result.driverMatches !== undefined && (
                <View style={{ alignItems: 'center' }}>
                  <Text style={{ fontSize: 24, fontWeight: 'bold', color: theme.colors.primary }}>
                    {result.driverMatches}
                  </Text>
                  <Text style={{ fontSize: 12, color: theme.colors.gray600 }}>Driver Matches</Text>
                </View>
              )}
            </View>
            
            {result.errors.length > 0 && (
              <View style={{ marginTop: 16 }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: theme.colors.error, marginBottom: 8 }}>
                  Errors ({result.errors.length}):
                </Text>
                <ScrollView style={{ maxHeight: 120 }}>
                  {result.errors.slice(0, 10).map((error, i) => (
                    <Text key={i} style={{ fontSize: 12, color: theme.colors.gray600, marginBottom: 4 }}>
                      • {error}
                    </Text>
                  ))}
                  {result.errors.length > 10 && (
                    <Text style={{ fontSize: 12, color: theme.colors.gray600, fontStyle: 'italic' }}>
                      ... and {result.errors.length - 10} more errors
                    </Text>
                  )}
                </ScrollView>
              </View>
            )}
          </View>
        ))}
      </View>
    );
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: theme.colors.gray50 }}>
      <View style={{ padding: 24 }}>
        {/* Header */}
        <View style={{ marginBottom: 32 }}>
          <Text style={{ fontSize: 24, fontWeight: 'bold', color: theme.colors.textPrimary, marginBottom: 8 }}>
            Data Import
          </Text>
          <Text style={{ fontSize: 16, color: theme.colors.gray600 }}>
            Upload CSV files to import operational data
          </Text>
        </View>

        {/* Supported File Types */}
        <View style={{
          backgroundColor: theme.colors.white,
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          borderWidth: 1,
          borderColor: theme.colors.gray200,
        }}>
          <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 16, color: theme.colors.textPrimary }}>
            Supported File Types
          </Text>
          
          <View style={{ gap: 12 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Ionicons name="document-text" size={20} color={theme.colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: '500', color: theme.colors.textPrimary }}>Salik Tolls</Text>
                <Text style={{ fontSize: 12, color: theme.colors.gray600 }}>
                  Headers: Expense Date, Plate Number, Location, Amount
                </Text>
              </View>
            </View>
            
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Ionicons name="document-text" size={20} color={theme.colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: '500', color: theme.colors.textPrimary }}>Traffic Fines</Text>
                <Text style={{ fontSize: 12, color: theme.colors.gray600 }}>
                  Headers: License Plate, Fine Amount, Violation Date, Location
                </Text>
              </View>
            </View>
            
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Ionicons name="document-text" size={20} color={theme.colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: '500', color: theme.colors.textPrimary }}>Uber Income</Text>
                <Text style={{ fontSize: 12, color: theme.colors.gray600 }}>
                  Headers: Driver UUID, Trip Date, Gross Amount, Net Amount
                </Text>
              </View>
            </View>
            
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Ionicons name="document-text" size={20} color={theme.colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: '500', color: theme.colors.textPrimary }}>Careem Income</Text>
                <Text style={{ fontSize: 12, color: theme.colors.gray600 }}>
                  Headers: Captain ID, Trip Date, Gross Amount, Net Amount
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Upload Button */}
        <TouchableOpacity
          onPress={handleFileUpload}
          disabled={progressState.isProcessing}
          style={{
            backgroundColor: progressState.isProcessing ? theme.colors.gray300 : theme.colors.primary,
            borderRadius: 12,
            padding: 16,
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            {progressState.isProcessing ? (
              <ActivityIndicator size="small" color={theme.colors.white} />
            ) : (
              <Ionicons name="cloud-upload" size={24} color={theme.colors.white} />
            )}
            <Text style={{
              fontSize: 16,
              fontWeight: '600',
              color: progressState.isProcessing ? theme.colors.gray600 : theme.colors.white,
            }}>
              {progressState.isProcessing ? 'Processing...' : 'Select CSV File'}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Progress */}
        {progressState.isProcessing && (
          <View style={{
            backgroundColor: theme.colors.white,
            borderRadius: 12,
            padding: 20,
            marginBottom: 24,
            borderWidth: 1,
            borderColor: theme.colors.gray200,
          }}>
            <Text style={{ fontSize: 16, fontWeight: '600', marginBottom: 12, color: theme.colors.textPrimary }}>
              Processing...
            </Text>
            
            <View style={{
              backgroundColor: theme.colors.gray200,
              borderRadius: 8,
              height: 8,
              marginBottom: 12,
            }}>
              <View style={{
                backgroundColor: theme.colors.primary,
                borderRadius: 8,
                height: 8,
                width: `${progressState.progress}%`,
              }} />
            </View>
            
            <Text style={{ fontSize: 14, color: theme.colors.gray600 }}>
              {progressState.currentStep} ({Math.round(progressState.progress)}%)
            </Text>
          </View>
        )}

        {/* Results */}
        {renderResults()}
      </View>
    </ScrollView>
  );
};
